// Plan configuration with all features and limits

export type PlanType = 'free' | 'starter' | 'professional' | 'business' | 'enterprise';

export interface PlanLimits {
  name: string;
  displayName: string;
  monthlyPrice: number;
  yearlyPrice: number;
  staticQRLimit: number; // -1 = unlimited
  dynamicQRLimit: number; // -1 = unlimited
  scansPerMonth: number; // -1 = unlimited
  analyticsHistoryDays: number; // -1 = unlimited
  batchLimit: number;
  aiSuggestionsPerMonth: number; // -1 = unlimited
  apiRequestsPerMonth: number; // 0 = no API access, -1 = unlimited
  teamMembers: number; // 1 = solo, -1 = unlimited
  downloadFormats: ('png' | 'svg' | 'pdf')[];
  features: {
    watermark: boolean;
    abTesting: boolean;
    customDomain: boolean;
    whiteLabel: boolean;
    prioritySupport: boolean;
    dedicatedSupport: boolean;
    slaGuarantee: boolean;
    customIntegrations: boolean;
    onPremise: boolean;
  };
  trialDays: number;
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
}

export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  free: {
    name: 'free',
    displayName: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    staticQRLimit: 5,
    dynamicQRLimit: 3,
    scansPerMonth: 50,
    analyticsHistoryDays: 7,
    batchLimit: 0,
    aiSuggestionsPerMonth: 3, // Free users get 3 AI suggestions per month
    apiRequestsPerMonth: 0,
    teamMembers: 1,
    downloadFormats: ['png'],
    features: {
      watermark: true,
      abTesting: false,
      customDomain: false,
      whiteLabel: false,
      prioritySupport: false,
      dedicatedSupport: false,
      slaGuarantee: false,
      customIntegrations: false,
      onPremise: false,
    },
    trialDays: 0,
  },
  starter: {
    name: 'starter',
    displayName: 'Starter',
    monthlyPrice: 9,
    yearlyPrice: 90,
    staticQRLimit: -1,
    dynamicQRLimit: 50,
    scansPerMonth: 100000,
    analyticsHistoryDays: 90,
    batchLimit: 10,
    aiSuggestionsPerMonth: 3,
    apiRequestsPerMonth: 0,
    teamMembers: 1,
    downloadFormats: ['png', 'svg', 'pdf'],
    features: {
      watermark: false,
      abTesting: false,
      customDomain: false,
      whiteLabel: false,
      prioritySupport: false,
      dedicatedSupport: false,
      slaGuarantee: false,
      customIntegrations: false,
      onPremise: false,
    },
    trialDays: 0,
  },
  professional: {
    name: 'professional',
    displayName: 'Professional',
    monthlyPrice: 29,
    yearlyPrice: 290,
    staticQRLimit: -1,
    dynamicQRLimit: 250,
    scansPerMonth: -1,
    analyticsHistoryDays: -1,
    batchLimit: 100,
    aiSuggestionsPerMonth: -1,
    apiRequestsPerMonth: 10000,
    teamMembers: 1,
    downloadFormats: ['png', 'svg', 'pdf'],
    features: {
      watermark: false,
      abTesting: true,
      customDomain: false,
      whiteLabel: false,
      prioritySupport: true,
      dedicatedSupport: false,
      slaGuarantee: false,
      customIntegrations: false,
      onPremise: false,
    },
    trialDays: 0,
  },
  business: {
    name: 'business',
    displayName: 'Business',
    monthlyPrice: 79,
    yearlyPrice: 790,
    staticQRLimit: -1,
    dynamicQRLimit: -1,
    scansPerMonth: -1,
    analyticsHistoryDays: -1,
    batchLimit: 500,
    aiSuggestionsPerMonth: -1,
    apiRequestsPerMonth: 100000,
    teamMembers: 5,
    downloadFormats: ['png', 'svg', 'pdf'],
    features: {
      watermark: false,
      abTesting: true,
      customDomain: true,
      whiteLabel: true,
      prioritySupport: true,
      dedicatedSupport: true,
      slaGuarantee: false,
      customIntegrations: false,
      onPremise: false,
    },
    trialDays: 0,
  },
  enterprise: {
    name: 'enterprise',
    displayName: 'Enterprise',
    monthlyPrice: 0, // Custom pricing
    yearlyPrice: 0,
    staticQRLimit: -1,
    dynamicQRLimit: -1,
    scansPerMonth: -1,
    analyticsHistoryDays: -1,
    batchLimit: -1,
    aiSuggestionsPerMonth: -1,
    apiRequestsPerMonth: -1,
    teamMembers: -1,
    downloadFormats: ['png', 'svg', 'pdf'],
    features: {
      watermark: false,
      abTesting: true,
      customDomain: true,
      whiteLabel: true,
      prioritySupport: true,
      dedicatedSupport: true,
      slaGuarantee: true,
      customIntegrations: true,
      onPremise: true,
    },
    trialDays: 0,
  },
};

export function getPlanLimits(plan: string): PlanLimits {
  return PLAN_LIMITS[plan as PlanType] || PLAN_LIMITS.free;
}

export function formatLimit(value: number): string {
  if (value === -1) return 'Unlimited';
  if (value === 0) return '—';
  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
}

export function isFeatureAvailable(plan: PlanType, feature: keyof PlanLimits['features']): boolean {
  return PLAN_LIMITS[plan].features[feature];
}

export function canUpgradeTo(currentPlan: PlanType, targetPlan: PlanType): boolean {
  const planOrder: PlanType[] = ['free', 'starter', 'professional', 'business', 'enterprise'];
  return planOrder.indexOf(targetPlan) > planOrder.indexOf(currentPlan);
}

export function getNextPlan(currentPlan: PlanType): PlanType | null {
  const planOrder: PlanType[] = ['free', 'starter', 'professional', 'business', 'enterprise'];
  const currentIndex = planOrder.indexOf(currentPlan);
  if (currentIndex < planOrder.length - 1) {
    return planOrder[currentIndex + 1];
  }
  return null;
}
